import { noop, useIntl, type UserRequirementStateStatus } from '@capsicum/common';
import { useInfoModal } from '@capsicum/common-web';

import { UkQuizTryAgainModal } from '../ui/uk-regulations/uk-quiz/UkQuizTryAgainModal';

type UkQuizModalsProps = {
	isForcedRequirement: boolean;
	onDismiss: () => void;
	status?: UserRequirementStateStatus;
};

export const useUkQuizModals = ({ isForcedRequirement, onDismiss, status }: UkQuizModalsProps) => {
	const { $t } = useIntl();

	const isUkQuizFailed = status === 'retry';
	const { showInfoModal } = useInfoModal();

	const showBlockedUntilModal = (attempts: null | number, blockedUntil: string) => {
		if (!blockedUntil) return;

		showInfoModal({
			buttonOnHandlers: {
				onPrimaryButtonHandler: noop
			},
			buttonTexts: {
				primaryButtonText: $t('global.button.close')
			},
			content: <UkQuizTryAgainModal attempts={attempts} blockedUntil={blockedUntil} />,
			image: 'warning',
			onDismiss,
			testID: 'uk-quiz-blocked-until-modal',
			variant: 'alert'
		});
	};

	const showIntroductoryModal = (onProceedPress: () => void) => {
		showInfoModal({
			buttonOnHandlers: {
				onPrimaryButtonHandler: onProceedPress,
				primaryInvokeOnDismiss: false
			},
			buttonTexts: {
				primaryButtonText: $t(`global.button.${isUkQuizFailed ? 'retry' : 'completeItNow'}`)
			},
			image: isUkQuizFailed ? 'quizRetry' : 'questionnaire',
			isClosable: !isForcedRequirement,
			onDismiss,
			subtitle: $t(
				`jurisdictionRequirements.ukQuiz.${isUkQuizFailed ? 'failedModalDescription' : 'startModalText'}`
			),
			testID: 'uk-quiz-introduction-modal',
			title: isUkQuizFailed ? $t('jurisdictionRequirements.ukQuiz.failedModalTitle') : '',
			variant: 'confirm'
		});
	};

	const showSuccessModal = (onClose: () => void) => {
		showInfoModal({
			buttonOnHandlers: {
				onPrimaryButtonHandler: onClose
			},
			buttonTexts: {
				primaryButtonText: $t('global.close')
			},
			image: 'quizSuccess',
			subtitle: $t('jurisdictionRequirements.ukQuiz.passedModalText'),
			testID: 'uk-quiz-success-modal',
			variant: 'alert'
		});
	};

	return {
		showBlockedUntilModal,
		showIntroductoryModal,
		showSuccessModal
	};
};
